import { log } from '@js/utils/logger'
import { placeOrder } from '@js/api/checkout/placeOrder'
import { setPaymentMethodOnCart } from '@js/api/cart/setPaymentMethodOnCart'

import { Cart } from '@js/api/types/cart/Cart'
import { AffirmCheckoutData } from '@js/types/Affirm'
import { Customer } from '@js/api/types/customer/Customer'
import { CustomerRegistration } from '@js/api/types/customer/CustomerRegistration'

interface PrepareAffirmCheckoutDataPayload {
  cart: Cart
  user: Customer
  registration: CustomerRegistration | null
  urls: {
    cancel: string
    confirmation: string
  }
}

interface HandleAffirmSuccessPayload {
  cart: Cart
  cartId: string
  checkoutToken: string
  onSuccess: (orderNumber: string) => void
  onError: (errorMessage: string) => void
  registration: CustomerRegistration | null
  payingBy: string
}

interface InitiateAffirmCheckoutPayload extends PrepareAffirmCheckoutDataPayload {
  onSuccess: (orderNumber: string) => void
  onError: (errorMessage: string) => void
  payingBy: string 
}

export function prepareAffirmCheckoutData(payload: PrepareAffirmCheckoutDataPayload): AffirmCheckoutData {
  const { cart, registration, urls, user } = payload

  const addressInfo = {
    name: {
      full: `${user.firstname} ${user.lastname}`,
    },
    address: {
      line1: registration?.personal?.address || '',
      line2: registration?.personal?.address_two || '',
      city: registration?.personal?.city || '',
      state: registration?.personal?.state || '',
      zipcode: registration?.personal?.postcode || '',
      country: registration?.personal?.country || 'USA',
    },
  }

  return {
    merchant: {
      user_cancel_url: urls.cancel,
      user_confirmation_url: urls.confirmation,
    },
    shipping: addressInfo,
    billing: {
      email: cart.email,
      ...addressInfo,
    },
    items: cart.items.map(item => ({
      qty: 1,
      sku: item.product.sku,
      display_name: item.product.name,
      unit_price: item.prices.price.value * 100,
    })),
    metadata: {
      mode: 'modal',
      platform_type: 'magento',
      shipping_type: 'digital',
    },
    total: cart.prices.grand_total.value * 100,
  }
}

export async function handleAffirmSuccess(payload: HandleAffirmSuccessPayload): Promise<void> {
  try {
    await setPaymentMethodOnCart({
      cartId: payload.cartId,
      paymentMethod: {
        code: 'affirm_gateway',
        affirm: {
          checkout_token: payload.checkoutToken,
        },
      },
    })

    const orderNumber = await placeOrder({
      quantity: 1,
      cartId: payload.cartId,
      sku: payload.registration?.item.sku ?? '',
      registrationId: payload.registration?.registration_id ?? 0,
      courseOption: payload.registration?.item.course_option ?? '',
      paying_by: payload.payingBy ?? 'myself',
    })

    if (orderNumber) {
      payload.onSuccess(String(orderNumber))
    } else {
      payload.onError('An error occurred while processing your order.')

      log({
        message: 'Error placing order for Affirm',
        file: 'src/wcei/js/utils/affirmHelpers.ts',
      })
    }
  } catch (error) {
    payload.onError('An error occurred while processing your Affirm payment.')

    log({
      message: 'Error setting payment method or placing order',
      file: 'src/wcei/js/utils/affirmHelpers.ts',
    })
  }
}

export function handleAffirmFailure(onError: (errorMessage: string) => void): void {
  onError('The Affirm checkout was canceled or failed.')

  log({
    message: 'Error completing Affirm checkout',
    file: 'src/wcei/js/utils/affirmHelpers.ts',
  })
}

export function initiateAffirmCheckout(payload: InitiateAffirmCheckoutPayload): void {
  try {
    const checkoutData = prepareAffirmCheckoutData({
      cart: payload.cart,
      user: payload.user,
      registration: payload.registration,
      urls: {
        cancel: payload.urls.cancel,
        confirmation: payload.urls.confirmation,
      },
    })

    window.affirm.checkout(checkoutData)

    window.affirm.checkout.open({
      onFail: () => handleAffirmFailure(payload.onError),
      onSuccess: event => handleAffirmSuccess({
        cart: payload.cart,
        cartId: payload.cart.id,
        onError: payload.onError,
        onSuccess: payload.onSuccess,
        registration: payload.registration,
        checkoutToken: event.checkout_token,
        payingBy: payload.payingBy,
      }),
    })
  } catch (error) {
    payload.onError('An error occurred while initializing Affirm payment.')

    log({
      message: 'Error setting up Affirm payment',
      file: 'src/wcei/js/utils/affirmHelpers.ts',
    })
  }
}
